import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { BsArrowDown, BsArrowUp, BsPlusCircle, BsTrash } from 'react-icons/bs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input, fieldControlClassName } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import SearchableSelect from '../../../components/shared/SearchableSelect';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Check, ChevronDown } from 'lucide-react';
import { fetchRoutingMids, fetchRoutingFallbackMessage, setRoutingFallbackMessage, fetchRiskFields, fetchRoutingMerchantTerminals } from '../api/api';
import { showToast } from '../../../store/toastStore';
import type { RiskField, RoutingMid, RoutingRule, RoutingRulePayload } from '../models/types';

const DEFAULT_DENIAL_MESSAGE = 'violation, this transaction cannot be completed.';
import type { MerchantOption } from '../PaymentRouting';

/** A single editable condition group in the dialog. Amounts are kept as strings while typing. */
interface GroupDraft {
  countryCodes: string;
  countryOperator: 'IN' | 'NOT_IN';
  currencies: string;
  currencyOperator: 'IN' | 'NOT_IN';
  /** Email condition toggled on (its value is typed inline). Independent of the risk condition —
   *  a group can have both. */
  emailEnabled: boolean;
  userEmails: string;
  userEmailOperator: 'IN' | 'NOT_IN';
  /** Comma list of RiskRuleType values; empty = no risk condition. */
  riskRules: string;
  riskRulesOperator: 'IN' | 'NOT_IN';
  /** Comma list of MOBILE/DESKTOP/TABLET; empty means "any device". */
  deviceTypes: string;
  minAmount: string;
  maxAmount: string;
  targetMappingId: number | null;
}

/** Device options for the per-group selector. Empty selection = any device. */
const OPERATOR_OPTIONS: ReadonlyArray<{ value: 'IN' | 'NOT_IN'; label: string }> = [
  { value: 'IN', label: 'IN' },
  { value: 'NOT_IN', label: 'NOT IN' },
];

function OperatorSelect({
  value,
  onChange,
  label,
}: {
  value: 'IN' | 'NOT_IN';
  onChange: (value: 'IN' | 'NOT_IN') => void;
  label: string;
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as 'IN' | 'NOT_IN')}>
      <SelectTrigger className="h-9 w-[104px] shrink-0" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {OPERATOR_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const DEVICE_OPTIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'MOBILE', label: 'Mobile' },
  { code: 'DESKTOP', label: 'Desktop' },
  { code: 'TABLET', label: 'Tablet' },
];

/**
 * Cascading, searchable condition picker for the User/Risk slot. A single dropdown whose two
 * categories — "User fields" and "Risk fields" — each reveal a searchable, multi-select checklist as
 * you hover them. Items from BOTH categories can be checked (they AND together on the group). The
 * "Customer email" item just toggles the email condition on; its value is typed inline below.
 */
function ConditionFieldPicker({
  emailEnabled,
  onToggleEmail,
  riskSelected,
  onToggleRisk,
  riskFields,
}: {
  emailEnabled: boolean;
  onToggleEmail: () => void;
  riskSelected: string[];
  onToggleRisk: (value: string) => void;
  riskFields: RiskField[];
}) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<'user' | 'risk'>('user');
  const [search, setSearch] = useState('');

  // Radix Popover inside the Dialog swallows wheel events, so native overflow scrolling is dead.
  // Drive the list's scroll manually with a non-passive wheel listener (same fix as SearchableSelect).
  const detachWheelRef = useRef<(() => void) | null>(null);
  const bindListRef = useCallback((node: HTMLDivElement | null) => {
    detachWheelRef.current?.();
    detachWheelRef.current = null;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      const maxScroll = node.scrollHeight - node.clientHeight;
      if (maxScroll > 0) {
        node.scrollTop = Math.min(maxScroll, Math.max(0, node.scrollTop + event.deltaY));
      }
      event.preventDefault();
      event.stopPropagation();
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    detachWheelRef.current = () => node.removeEventListener('wheel', onWheel);
  }, []);

  const userItems = [{ value: 'EMAIL', label: 'Customer email', checked: emailEnabled }];
  const riskItems = riskFields.map((rf) => ({
    value: rf.value,
    label: rf.label,
    checked: riskSelected.includes(rf.value),
  }));
  const items = category === 'user' ? userItems : riskItems;
  const query = search.trim().toLowerCase();
  const filtered = query
    ? items.filter((it) => `${it.label} ${it.value}`.toLowerCase().includes(query))
    : items;

  const selectedCount = (emailEnabled ? 1 : 0) + riskSelected.length;
  const summary = selectedCount === 0
    ? 'Add a user or risk condition'
    : [emailEnabled ? 'Email' : null, riskSelected.length ? `${riskSelected.length} risk rule${riskSelected.length > 1 ? 's' : ''}` : null]
        .filter(Boolean)
        .join(' · ');

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
      modal={false}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(fieldControlClassName, 'flex items-center justify-between gap-2 text-left')}
        >
          <span className={cn('min-w-0 flex-1 truncate', selectedCount === 0 && 'text-muted-foreground')}>
            {summary}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        portal
        align="start"
        collisionPadding={8}
        className="z-[100] flex w-[min(40rem,calc(100vw-2rem))] overflow-hidden p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onWheel={(e) => e.stopPropagation()}
      >
        {/* Left: categories (hover to switch). Right: searchable multi-select checklist. */}
        <div className="w-44 shrink-0 border-r border-border bg-muted/30 py-1">
          {([
            { key: 'user', label: 'User fields', count: emailEnabled ? 1 : 0 },
            { key: 'risk', label: 'Risk fields', count: riskSelected.length },
          ] as const).map((cat) => (
            <button
              key={cat.key}
              type="button"
              onMouseEnter={() => {
                setCategory(cat.key);
                setSearch('');
              }}
              onClick={() => {
                setCategory(cat.key);
                setSearch('');
              }}
              className={cn(
                'flex w-full items-center justify-between gap-1 px-3 py-2 text-left text-sm',
                category === cat.key ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50',
              )}
            >
              <span className="truncate">{cat.label}</span>
              {cat.count > 0 && (
                <span className="rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">
                  {cat.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="border-b border-border p-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={category === 'user' ? 'Search user fields…' : 'Search risk rules…'}
              aria-label="Search condition fields"
              autoComplete="off"
              className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <div ref={bindListRef} className="max-h-72 min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">No matches.</p>
            ) : (
              filtered.map((it) => (
                <button
                  key={it.value}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={it.checked}
                  onClick={() => (category === 'user' ? onToggleEmail() : onToggleRisk(it.value))}
                  className="relative flex w-full items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      it.checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                    )}
                  >
                    {it.checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{it.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function parseDevices(value: string): Set<string> {
  return new Set(
    value
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s && s !== '*'),
  );
}

function emptyGroup(): GroupDraft {
  return {
    countryCodes: '',
    countryOperator: 'IN',
    currencies: '',
    currencyOperator: 'IN',
    emailEnabled: false,
    userEmails: '',
    userEmailOperator: 'IN',
    riskRules: '',
    riskRulesOperator: 'IN',
    deviceTypes: '',
    minAmount: '0',
    maxAmount: '100000',
    targetMappingId: null,
  };
}

/**
 * The Routing Rule dialog. The merchant is chosen HERE (admins), and its MIDs load accordingly — no
 * need to pick a merchant elsewhere first. One rule per merchant; the rule is a name plus an ordered
 * list of condition groups (OR-branches, first match wins) and one global fallback MID.
 */
export default function RoutingRuleDialog({
  rule,
  merchants,
  isMerchantUser,
  onCancel,
  onSubmit,
}: {
  rule: RoutingRule | null;
  merchants: MerchantOption[];
  isMerchantUser: boolean;
  onCancel: () => void;
  onSubmit: (payload: RoutingRulePayload) => void;
}) {
  const [merchantId, setMerchantId] = useState<number | null>(rule?.merchantId ?? null);
  // Optional terminal scope. null = merchant-wide (applies to all terminals, the default). When set,
  // the rule applies only to payments on that merchant terminal.
  const [merchantTerminalId, setMerchantTerminalId] = useState<number | null>(
    rule?.merchantTerminalId ?? null,
  );
  const [name, setName] = useState(rule?.name ?? '');
  const [active, setActive] = useState(rule?.active ?? true);
  const [groups, setGroups] = useState<GroupDraft[]>(
    rule && rule.groups.length > 0
      ? rule.groups.map((g) => ({
          countryCodes: g.countryCodes ?? '',
          countryOperator: g.countryOperator === 'NOT_IN' ? 'NOT_IN' : 'IN',
          currencies: g.currencies ?? '',
          currencyOperator: g.currencyOperator === 'NOT_IN' ? 'NOT_IN' : 'IN',
          emailEnabled: !!(g.userEmails && g.userEmails !== '*'),
          userEmails: g.userEmails && g.userEmails !== '*' ? g.userEmails : '',
          userEmailOperator: g.userEmailOperator === 'NOT_IN' ? 'NOT_IN' : 'IN',
          riskRules: g.riskRules && g.riskRules !== '*' ? g.riskRules : '',
          riskRulesOperator: g.riskRulesOperator === 'NOT_IN' ? 'NOT_IN' : 'IN',
          deviceTypes: g.deviceTypes && g.deviceTypes !== '*' ? g.deviceTypes : '',
          minAmount: String(g.minAmount ?? 0),
          maxAmount: String(g.maxAmount ?? 0),
          targetMappingId: g.targetMappingId,
        }))
      : [emptyGroup()],
  );
  const [fallbackMappingId, setFallbackMappingId] = useState<number | null>(
    rule?.fallbackMappingId ?? null,
  );
  const [mids, setMids] = useState<RoutingMid[]>([]);
  const [midsLoading, setMidsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Catalog of risk-rule fields a group can route on (RiskRuleType value + label). Loaded once.
  const [riskFields, setRiskFields] = useState<RiskField[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchRiskFields()
      .then((fields) => {
        if (!cancelled) setRiskFields(fields);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // The single GLOBAL denial message, shared by every rule. Shown here because a block group (no
  // Route-to-MID) or no-fallback rejection uses it. Editing it changes it for ALL rules.
  const [denialMsg, setDenialMsg] = useState(DEFAULT_DENIAL_MESSAGE);
  const [savedDenialMsg, setSavedDenialMsg] = useState(DEFAULT_DENIAL_MESSAGE);
  const [savingDenial, setSavingDenial] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchRoutingFallbackMessage()
      .then((msg) => {
        if (cancelled) return;
        const v = msg || DEFAULT_DENIAL_MESSAGE;
        setDenialMsg(v);
        setSavedDenialMsg(v);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const denialDirty = denialMsg.trim() !== savedDenialMsg.trim();
  const hasBlockGroup = groups.some((g) => g.targetMappingId == null);
  const saveDenialMsg = async () => {
    setSavingDenial(true);
    try {
      const saved = await setRoutingFallbackMessage(denialMsg.trim());
      const v = saved || denialMsg.trim();
      setSavedDenialMsg(v);
      showToast('Denial message updated for all rules.', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to update denial message.', 'error');
    } finally {
      setSavingDenial(false);
    }
  };

  const isEdit = !!rule;
  // MIDs load for a merchant user (scoped) or once an admin has picked a merchant.
  const merchantChosen = isMerchantUser || merchantId !== null;

  useEffect(() => {
    if (!merchantChosen) {
      setMids([]);
      return;
    }
    let cancelled = false;
    setMidsLoading(true);
    fetchRoutingMids(merchantId)
      .then((list) => {
        if (!cancelled) setMids(list);
      })
      .catch(() => {
        if (!cancelled) setMids([]);
      })
      .finally(() => {
        if (!cancelled) setMidsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [merchantId, merchantChosen]);

  const midOptions = mids.map((mid) => ({ value: String(mid.mappingId), label: mid.label }));

  // ALL the merchant's terminals (not just those with a MID), fetched directly so every terminal
  // shows as a scope option. Loads alongside the MIDs whenever a merchant is chosen.
  const [terminals, setTerminals] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    if (!merchantChosen) {
      setTerminals([]);
      return;
    }
    let cancelled = false;
    fetchRoutingMerchantTerminals(merchantId)
      .then((list) => {
        if (!cancelled) setTerminals(list);
      })
      .catch(() => {
        if (!cancelled) setTerminals([]);
      });
    return () => {
      cancelled = true;
    };
  }, [merchantId, merchantChosen]);

  // Terminal-scope options: every merchant terminal. "ALL" = merchant-wide. In edit mode, keep the
  // rule's current terminal selectable even if it was since deleted.
  const ALL_TERMINALS = 'ALL';
  const terminalOptions = (() => {
    const byId = new Map<number, string>();
    if (rule?.merchantTerminalId != null) {
      byId.set(rule.merchantTerminalId, rule.merchantTerminalName ?? `Terminal ${rule.merchantTerminalId}`);
    }
    for (const t of terminals) {
      if (!byId.has(t.id)) byId.set(t.id, t.name);
    }
    return [
      { value: ALL_TERMINALS, label: 'All terminals (merchant-wide)' },
      ...Array.from(byId.entries()).map(([id, label]) => ({ value: String(id), label })),
    ];
  })();

  const updateGroup = (index: number, patch: Partial<GroupDraft>) => {
    setGroups((current) => current.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  };
  const toggleDevice = (index: number, code: string) => {
    setGroups((current) =>
      current.map((g, i) => {
        if (i !== index) return g;
        const selected = parseDevices(g.deviceTypes);
        if (selected.has(code)) selected.delete(code);
        else selected.add(code);
        return { ...g, deviceTypes: Array.from(selected).join(',') };
      }),
    );
  };
  const toggleRisk = (index: number, value: string) => {
    setGroups((current) =>
      current.map((g, i) => {
        if (i !== index) return g;
        const selected = new Set(
          g.riskRules.split(',').map((s) => s.trim()).filter(Boolean),
        );
        if (selected.has(value)) selected.delete(value);
        else selected.add(value);
        return { ...g, riskRules: Array.from(selected).join(',') };
      }),
    );
  };
  const addGroup = () => setGroups((current) => [...current, emptyGroup()]);
  const removeGroup = (index: number) =>
    setGroups((current) => (current.length <= 1 ? current : current.filter((_, i) => i !== index)));
  const moveGroup = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= groups.length) return;
    setGroups((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // Changing the merchant invalidates every MID selection (they belong to the old merchant).
  const onMerchantChange = (value: string) => {
    setMerchantId(Number(value) || null);
    setMerchantTerminalId(null);
    setGroups((current) => current.map((g) => ({ ...g, targetMappingId: null })));
    setFallbackMappingId(null);
  };

  const submit = () => {
    if (!isMerchantUser && merchantId === null) return setError('Select a merchant.');
    if (!name.trim()) return setError('Rule name is required.');
    if (groups.length === 0) return setError('Add at least one condition group.');

    for (let i = 0; i < groups.length; i += 1) {
      const g = groups[i];
      const label = `Group ${i + 1}`;
      if (g.emailEnabled && !g.userEmails.trim()) {
        return setError(`${label}: enter at least one user email, or remove the email condition.`);
      }
      // Route to MID is optional: null = block (a matching payment is denied with the global message).
      const min = Number(g.minAmount);
      const max = Number(g.maxAmount);
      if (Number.isNaN(min) || Number.isNaN(max)) return setError(`${label}: amounts must be numbers.`);
      if (min > max) return setError(`${label}: minimum amount cannot be greater than maximum.`);
    }

    setError(null);
    onSubmit({
      merchantId: isMerchantUser ? null : merchantId,
      merchantTerminalId,
      name: name.trim(),
      active,
      groups: groups.map((g) => ({
        countryCodes: g.countryCodes.trim().toUpperCase(),
        countryOperator: g.countryOperator,
        currencies: g.currencies.trim().toUpperCase(),
        currencyOperator: g.currencyOperator,
        userEmails: g.emailEnabled ? g.userEmails.trim().toLowerCase() : '*',
        userEmailOperator: g.emailEnabled ? g.userEmailOperator : 'IN',
        riskRules: g.riskRules.trim() ? g.riskRules.trim() : '*',
        riskRulesOperator: g.riskRules.trim() ? g.riskRulesOperator : 'IN',
        // Empty selection = any device.
        deviceTypes: g.deviceTypes.trim() ? g.deviceTypes.trim().toUpperCase() : '*',
        minAmount: Number(g.minAmount),
        maxAmount: Number(g.maxAmount),
        targetMappingId: g.targetMappingId,
      })),
      fallbackMappingId,
    });
  };

  const midSelectPlaceholder = midsLoading
    ? 'Loading MIDs…'
    : merchantChosen
      ? 'Select MID'
      : 'Pick a merchant first';

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="flex max-h-[88vh] w-[92vw] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 pb-4 pt-6 text-left">
          <DialogTitle>{isEdit ? 'Edit Routing Rule' : 'New Routing Rule'}</DialogTitle>
          <DialogDescription>
            One rule per merchant, plus optionally one per terminal. Groups are checked top to bottom
            and joined by OR — the first group whose conditions all match wins. If none match, the
            global fallback is used.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
          {!isMerchantUser && (
            <div className="flex flex-col gap-1.5">
              <Label>Merchant</Label>
              {isEdit ? (
                <Input value={rule?.merchantName ?? '—'} disabled />
              ) : (
                <SearchableSelect
                  className="h-9"
                  value={merchantId != null ? String(merchantId) : ''}
                  options={merchants.map((m) => ({ value: String(m.id), label: m.name }))}
                  onChange={onMerchantChange}
                  placeholder="Select merchant"
                  searchPlaceholder="Search merchant…"
                  emptyMessage="No merchants available"
                  aria-label="Merchant"
                />
              )}
            </div>
          )}

          {merchantChosen && (
            <div className="flex flex-col gap-1.5">
              <Label>Merchant Terminal <span className="text-muted-foreground">(optional)</span></Label>
              <SearchableSelect
                className="h-9"
                value={merchantTerminalId != null ? String(merchantTerminalId) : ALL_TERMINALS}
                options={terminalOptions}
                onChange={(value) =>
                  setMerchantTerminalId(value === ALL_TERMINALS ? null : Number(value) || null)
                }
                placeholder="All terminals (merchant-wide)"
                searchPlaceholder="Search terminal…"
                emptyMessage={midsLoading ? 'Loading terminals…' : 'No terminals available'}
                aria-label="Merchant terminal"
              />
              <p className="text-xs text-muted-foreground">
                Leave as “All terminals” to apply this rule to the whole merchant. Pick a terminal to
                enforce it only for payments on that terminal.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule-name">Rule Name</Label>
            <Input
              id="rule-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Merchant routing"
            />
          </div>

          <div className="flex flex-col">
            {groups.map((group, index) => {
              return (
                <Fragment key={index}>
                  {index > 0 && (
                    <div className="flex items-center gap-2 py-1.5" aria-label="or" role="separator">
                      <div className="h-px flex-1 bg-border" />
                      <span className="rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary">
                        or
                      </span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                  )}
                  <div className="rounded-lg border border-border bg-muted/20 p-3.5 flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {index === 0 ? 'If — Group 1' : `Else if — Group ${index + 1}`}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          aria-label={`Move group ${index + 1} up`}
                          disabled={index === 0}
                          onClick={() => moveGroup(index, -1)}
                        >
                          <BsArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          aria-label={`Move group ${index + 1} down`}
                          disabled={index === groups.length - 1}
                          onClick={() => moveGroup(index, 1)}
                        >
                          <BsArrowDown className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6 text-destructive"
                          aria-label={`Remove group ${index + 1}`}
                          disabled={groups.length <= 1}
                          onClick={() => removeGroup(index)}
                        >
                          <BsTrash className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3.5">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`country-${index}`}>Countries (ISO)</Label>
                        <div className="flex gap-2">
                          <OperatorSelect
                            label="Country operator"
                            value={group.countryOperator}
                            onChange={(countryOperator) => updateGroup(index, { countryOperator })}
                          />
                          <Input
                            className="min-w-0 flex-1"
                            id={`country-${index}`}
                            value={group.countryCodes}
                            onChange={(e) => updateGroup(index, { countryCodes: e.target.value })}
                            placeholder="IN, US, GB"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Comma-separated. &quot;EU&quot; = euro area, &quot;*&quot; or blank = any.
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`currency-${index}`}>Currencies</Label>
                        <div className="flex gap-2">
                          <OperatorSelect
                            label="Currency operator"
                            value={group.currencyOperator}
                            onChange={(currencyOperator) => updateGroup(index, { currencyOperator })}
                          />
                          <Input
                            className="min-w-0 flex-1"
                            id={`currency-${index}`}
                            value={group.currencies}
                            onChange={(e) => updateGroup(index, { currencies: e.target.value })}
                            placeholder="INR, USD"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Comma-separated. &quot;*&quot; or blank = any.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>User / Risk fields</Label>
                      <ConditionFieldPicker
                        emailEnabled={group.emailEnabled}
                        onToggleEmail={() =>
                          updateGroup(index, {
                            emailEnabled: !group.emailEnabled,
                            // Clearing the toggle drops any typed emails so the condition is truly off.
                            ...(group.emailEnabled ? { userEmails: '' } : {}),
                          })
                        }
                        riskSelected={group.riskRules.split(',').map((s) => s.trim()).filter(Boolean)}
                        onToggleRisk={(value) => toggleRisk(index, value)}
                        riskFields={riskFields}
                      />
                      {group.emailEnabled && (
                        <>
                          <div className="flex gap-2">
                            <OperatorSelect
                              label="User email operator"
                              value={group.userEmailOperator}
                              onChange={(userEmailOperator) => updateGroup(index, { userEmailOperator })}
                            />
                            <Input
                              className="min-w-0 flex-1"
                              value={group.userEmails}
                              onChange={(e) => updateGroup(index, { userEmails: e.target.value })}
                              placeholder="a@example.com, b@example.com"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Comma-separated customer emails (case-insensitive).
                          </p>
                        </>
                      )}
                      {group.riskRules.trim() && (
                        <>
                          <div className="flex items-center gap-2">
                            <OperatorSelect
                              label="Risk rules operator"
                              value={group.riskRulesOperator}
                              onChange={(riskRulesOperator) => updateGroup(index, { riskRulesOperator })}
                            />
                            <span className="text-xs text-muted-foreground">
                              {group.riskRulesOperator === 'NOT_IN'
                                ? 'none of the selected risk rules fired'
                                : 'any of the selected risk rules fired'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Routes a flagged payment (e.g. too many attempts, VPN/proxy). Velocity rules
                            trigger on the Nth attempt per the risk rule’s threshold.
                          </p>
                        </>
                      )}
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>Device type</Label>
                      <div className="flex flex-wrap gap-2">
                        {DEVICE_OPTIONS.map((opt) => {
                          const on = parseDevices(group.deviceTypes).has(opt.code);
                          return (
                            <button
                              key={opt.code}
                              type="button"
                              onClick={() => toggleDevice(index, opt.code)}
                              aria-pressed={on}
                              className={
                                'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ' +
                                (on
                                  ? 'border-primary bg-primary/10 text-primary'
                                  : 'border-border bg-transparent text-muted-foreground hover:text-foreground')
                              }
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Select one or more, or leave all off for any device. Derived from the
                        shopper&rsquo;s browser — API / server-to-server traffic has no device and
                        won&rsquo;t match a device-specific group.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3.5">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`min-${index}`}>Min Amount</Label>
                        <Input
                          id={`min-${index}`}
                          type="number"
                          min="0"
                          value={group.minAmount}
                          onChange={(e) => updateGroup(index, { minAmount: e.target.value })}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`max-${index}`}>Max Amount</Label>
                        <Input
                          id={`max-${index}`}
                          type="number"
                          min="0"
                          value={group.maxAmount}
                          onChange={(e) => updateGroup(index, { maxAmount: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>Route to MID</Label>
                      <SearchableSelect
                        className="h-9"
                        value={group.targetMappingId != null ? String(group.targetMappingId) : ''}
                        options={[{ value: '', label: 'None (block — reject if this matches)' }, ...midOptions]}
                        onChange={(value) => updateGroup(index, { targetMappingId: Number(value) || null })}
                        placeholder={midSelectPlaceholder}
                        emptyMessage={merchantChosen ? 'No active MIDs' : 'Pick a merchant first'}
                        aria-label={`Route to MID for group ${index + 1}`}
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {group.targetMappingId == null
                        ? 'No MID selected → if this matches, the payment is blocked (rejected with the denial message below).'
                        : index === 0
                          ? 'If everything above matches, the payment routes here and no further groups are checked.'
                          : 'Only checked if the groups above did not match. If it matches, routes here and stops.'}
                    </p>
                  </div>
                </Fragment>
              );
            })}

            <Button variant="outline" className="self-start gap-1.5 mt-3" onClick={addGroup}>
              <BsPlusCircle className="h-3.5 w-3.5" /> Add condition group
            </Button>
          </div>

          <div className="flex flex-col gap-1.5 rounded-lg border border-dashed border-border bg-muted/20 p-3.5">
            <Label>Global fallback MID · “finally”</Label>
            <SearchableSelect
              className="h-9"
              value={fallbackMappingId != null ? String(fallbackMappingId) : ''}
              options={[{ value: '', label: 'No fallback (reject if nothing matches)' }, ...midOptions]}
              onChange={(value) => setFallbackMappingId(value ? Number(value) : null)}
              placeholder="No fallback (reject if nothing matches)"
              aria-label="Global fallback MID"
            />
            <p className="text-[11px] text-muted-foreground">
              Used only when <strong>none</strong> of the groups above match. It is never used when an
              acquirer declines a matched group. Leave empty to reject a payment that matches no group.
            </p>
          </div>

          <div className="flex flex-col gap-1.5 rounded-lg border border-dashed border-border bg-muted/20 p-3.5">
            <Label>Denial message{hasBlockGroup ? ' · used by this rule’s block' : ''}</Label>
            <Textarea
              value={denialMsg}
              onChange={(e) => setDenialMsg(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder={DEFAULT_DENIAL_MESSAGE}
              className="text-sm"
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] text-muted-foreground">
                Shown when a payment is rejected (a block group with no MID, or no group matched and no
                fallback). This message is <strong>global</strong> — it applies to every rule.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={saveDenialMsg}
                disabled={savingDenial || !denialDirty}
              >
                {savingDenial ? 'Saving…' : 'Save message'}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3.5 py-3">
            <Label htmlFor="rule-active" className="cursor-pointer">
              Rule Active
            </Label>
            <Switch id="rule-active" checked={active} onCheckedChange={setActive} />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-border px-6 py-4">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={submit}>{isEdit ? 'Save Changes' : 'Create Rule'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
